/** @jsx jsx */

import { useContext, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { State } from 'store/store';
import { InteractionContext } from '../../Layouts/ContainerLogical';
import { getBaseButtonStyles } from './getBaseButtonStyles';
import {
  parseStylesFromPropsAndTheme,
  updateButtonHeightForIE,
} from './buttonHelpers';
import hexToRgb from 'services/hexToRGB';
import { speechSynthesisCancel } from 'services/speechSynthesisCancel';

const { lPink } = colors;

const GenericButton = props => {
  //
  const interactionContext = useContext(InteractionContext) as any;

  const { focusedNodeId, theme } = useSelector((state: State) => {
    const { focusedNodeId, theme } = state;
    return {
      focusedNodeId,
      theme,
    };
  });

  const { id, label, enabled = undefined, onClick } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    color,
    background,
    fontFamily,
    buttonShape,
    borderRadius,
    buttonShadow,
    buttonOutline,
    hoverColor,
    focusColor,
    useLargeButton,
  } = parseStylesFromPropsAndTheme(props, theme);

  // console.log('generic button :', { focusColor });

  const selected = id === focusedNodeId;
  const disabled = enabled !== undefined ? !enabled : false;

  const baseButtonStyles = getBaseButtonStyles({
    color,
    buttonShape,
    borderRadius,
    background,
    fontFamily,
    hoverColor,
    buttonOutline,
    buttonShadow,
    useLargeButton,
  }) as any;

  const {
    includeButtonBackground = true,
    buttonBackgroundOpacity = 100,
    buttonHoverBackgroundOpacity = 80,
    fontSize = 14,
  } = props;

  const textHoverColor = props.textHoverColor || color;
  const buttonOutlineColor = buttonOutline
    ? props.buttonOutlineColor || background
    : 'transparent';

  const { r, g, b } = hexToRgb(background);

  let a = buttonBackgroundOpacity / 100;
  if (buttonOutline && props.buttonBackgroundOpacity === undefined) {
    a = 0;
  }

  // hoverColor might not be defined. so we'll grab the background color
  const hoverColor_ = hoverColor ? hoverColor : background;
  //
  const { r: hoverR, g: hoverG, b: hoverB } = hexToRgb(
    hoverColor_ === '#fff' ? '#ffffff' : hoverColor_
  );

  const hoverA = buttonHoverBackgroundOpacity / 100;

  const genericButton = (
    <button
      ref={buttonRef}
      id={`generic-button-${id}`}
      css={css({
        ...baseButtonStyles,
        fontSize,

        background: includeButtonBackground
          ? `rgba(${r}, ${g}, ${b}, ${a})` || background
          : 'transparent',

        border: `1px solid ${
          buttonOutline ? buttonOutlineColor : 'transparent'
        }`,

        transition: 'all 0.15s',

        ...(textHoverColor && {
          ':hover': {
            color: `${textHoverColor} !important`,
            background:
              `rgba(${hoverR}, ${hoverG}, ${hoverB}, ${hoverA})` || hoverColor,
          },
        }),

        ...(!window.dev() && disabled
          ? {
              // potentially want to be able to customize disabled color ?
              pointerEvents: 'none',
              opacity: 0.25,
            }
          : {
              ':focus-visible': {
                boxShadow: focusColor,
              },
            }),
      })}
      disabled={disabled}
      onClick={() => {
        // window.speechSynthesis.cancel();
        speechSynthesisCancel();
        onClick && onClick();
      }}
    >
      {label}
    </button>
  );

  updateButtonHeightForIE(buttonRef.current, buttonShape);

  return window.dev() ? (
    <div
      css={css({
        position: 'relative',
        display: 'inline-flex',

        ':hover': {
          button: {
            background:
              `rgba(${hoverR}, ${hoverG}, ${hoverB}, ${hoverA})` || hoverColor,
            color: `${textHoverColor} !important`,

            // border: `1px solid ${hoverColor || background}`,
          },
        },
      })}
    >
      {genericButton}

      <div
        css={css({
          position: 'absolute',
          left: -15,
          top: -10,
          width: 'calc(100% + 30px)',
          height: 'calc(100% + 20px)',
          boxShadow: selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey',
          transition: 'all 0.15s',
          cursor: 'pointer',

          ':hover': {
            boxShadow: `0 0 0 1px ${lPink}`,
          },
        })}
        onClick={e => {
          if (selected) {
            return;
          }

          setCurrentBlock({ id });
        }}
      >
        {!selected && <EditMarker />}
      </div>
    </div>
  ) : (
    genericButton
  );
};

export { GenericButton };
